import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, from, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, debounceTime, map } from 'rxjs/operators';

const pingUrl = 'https://www.google.com/generate_204';

@Injectable({ providedIn: 'root' })
export class NetworkService {
  isOnline$ = new BehaviorSubject<boolean>(true);

  constructor(
    private ngZone: NgZone,
    private readonly http: HttpClient
  ) {
    this.monitorNetworkStatus();
    this.startConnectivityPolling();
  }

  /** Eventos nativos online/offline */
  private monitorNetworkStatus() {
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$)
      .pipe(debounceTime(300))
      .subscribe(isOnline => this.updateStatus(isOnline));
  }

  private startConnectivityPolling() {
    let retryDelay = 2000; 

    const poll = () => {
      if (this.isOnline$.value) {
        retryDelay = 2000;
        timer(5000).subscribe(poll);
        return;
      }

      this.http.get(pingUrl, { responseType: 'text' })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        )
        .subscribe(isOnline => {
          if (isOnline) {
            retryDelay = 2000;
            this.updateStatus(true);
            timer(5000).subscribe(poll);
          } else {
            retryDelay = Math.min(retryDelay * 2, 30000);
            timer(retryDelay).subscribe(poll);
            console.log(`${NetworkService.name} Offline. Retrying in ${retryDelay}ms...`);
          }
        });
    };

    poll();
  }
  
  private updateStatus(isOnline: boolean) {
    this.ngZone.run(() => {
      if (this.isOnline$.value !== isOnline) {
        console.log(`${NetworkService.name} isConnected: ${isOnline}`);
        this.isOnline$.next(isOnline);
      }
    });
  }
}
